import { EntityUtils } from '@record-it-npm/synchro-client';
import React, { ComponentProps, useMemo, VoidFunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { CardListItemGroup } from 'record-it-components/cardList/CardListItemGroup/CardListItemGroup';
import { LocationContentContainer } from 'record-it-components/location/LocationContentContainer/LocationContentContainer';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { ConstructionSite } from '../../../classes/entities/ConstructionSite/constructionSiteTypes';
import { useConstructionSiteUsageStatisticTools } from '../../../classes/entities/ConstructionSiteUsageStatistic/useConstructionSiteUsageStatisticTools';
import { ConstructionSiteToSelectListItem } from '../../constructionSite/ConstructionSiteToSelectListItem/ConstructionSiteToSelectListItem';
import { NearbyConstructionSitesToSelectList } from '../../constructionSite/NearbyConstructionSitesToSelectList/NearbyConstructionSitesToSelectList';
import { useConstructionSiteSelectedHandler } from './useConstructionSiteSelectedHandler';

export type CreateWorkTimeEntrySelectConstructionSitePageDefaultContentProps =
  PropsWithoutChildren<ComponentProps<'div'>> & {
    constructionSites: Array<ConstructionSite>;
  };

export const CreateWorkTimeEntrySelectConstructionSitePageDefaultContent: VoidFunctionComponent<CreateWorkTimeEntrySelectConstructionSitePageDefaultContentProps> =
  React.memo((props) => {
    const { constructionSites, ...remainingProps } = prepareProps({
      props,
      ignoreChildren: true
    });

    const { t } = useTranslation();

    const { handleConstructionSiteSelected } =
      useConstructionSiteSelectedHandler();

    const usageStatisticTools = useConstructionSiteUsageStatisticTools();

    const mostUsedConstructionSites = useMemo(() => {
      return usageStatisticTools
        .getMostUsedConstructionSites(constructionSites)
        .slice(0, 5);
    }, [usageStatisticTools, constructionSites]);

    return (
      <div {...remainingProps}>
        <CardListItemGroup
          label={t(
            'components.page.CreateWorkTimeEntrySelectConstructionSitePage.groupHeaders.nearby'
          )}
          data-testid="CreateWorkTimeEntrySelectConstructionSitePage.NearbyConstructionSites"
        >
          <LocationContentContainer>
            {({ locationInfo }) => (
              <NearbyConstructionSitesToSelectList
                constructionSites={constructionSites}
                currentLocation={locationInfo}
                onConstructionSiteSelected={handleConstructionSiteSelected}
              ></NearbyConstructionSitesToSelectList>
            )}
          </LocationContentContainer>
        </CardListItemGroup>

        {mostUsedConstructionSites.length ? (
          <CardListItemGroup
            label={t(
              'components.page.CreateWorkTimeEntrySelectConstructionSitePage.groupHeaders.mostUsed'
            )}
            data-testid="CreateWorkTimeEntrySelectConstructionSitePage.MostUsedConstructionSites"
          >
            {mostUsedConstructionSites.map((constructionSite) => (
              <ConstructionSiteToSelectListItem
                key={EntityUtils.getTrackingKey(constructionSite)}
                constructionSite={constructionSite}
                onConstructionSiteSelected={handleConstructionSiteSelected}
                data-testid="CreateWorkTimeEntrySelectConstructionSitePage.MostUsedConstructionSite"
              ></ConstructionSiteToSelectListItem>
            ))}
          </CardListItemGroup>
        ) : null}

        <CardListItemGroup
          label={
            mostUsedConstructionSites.length
              ? t(
                  'components.page.CreateWorkTimeEntrySelectConstructionSitePage.groupHeaders.all'
                )
              : ''
          }
        >
          {constructionSites.map((constructionSite) => (
            <ConstructionSiteToSelectListItem
              key={EntityUtils.getTrackingKey(constructionSite)}
              constructionSite={constructionSite}
              onConstructionSiteSelected={handleConstructionSiteSelected}
              data-testid="CreateWorkTimeEntrySelectConstructionSitePage.ConstructionSiteListItem"
            ></ConstructionSiteToSelectListItem>
          ))}
        </CardListItemGroup>
      </div>
    );
  });
